import { useState } from "react";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronDown, Users } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { reviewerInitials, reviewerSlotLabel } from "../utils/reviewers";
import { useWorkflowConfig } from "../hooks/useWorkflow";
import { Tooltip } from "../ds";
import type { AssignedReviewer } from "../types/ess";

interface Props {
  reviewers: AssignedReviewer[];
}

// WC before SC — matches the assignment card's committee order. Labels are
// the fallback for a config without `committees`; the yaml's own names
// ("Working Committee", as on the Annexure and in print) win.
const COMMITTEE_ORDER: ReadonlyArray<{
  value: AssignedReviewer["committee"];
  label: string;
}> = [
  { value: "wc", label: "Working Committee" },
  { value: "sc", label: "Steering Committee" },
];

// Narrowest roster box that still fits two compact name columns (a 24px
// avatar and ~90px of name each) — the md+ rail at 1280px is ~280px wide.
// Below it the committees stack.
const TWO_COLUMN_MIN = 260;

/**
 * Read-only, collapsible summary of a suggestion's assigned reviewers,
 * shown in the right rail below Status History. Editing lives on the
 * left `CommitteeCard`; this is purely a glanceable roster. Hidden
 * entirely until at least one reviewer is assigned.
 *
 * Collapsed by default, like the email log below it: the header's count
 * says how many are seated. The detail page keys it on the suggestion, so
 * each suggestion opens collapsed.
 */
export default function RailReviewers({ reviewers }: Props) {
  const [open, setOpen] = useState(false);
  // Committee slots are named in the workflow config, so a roster row can
  // say "Convener" rather than the coarser "SC Member" its sub_role implies.
  const { data: workflow } = useWorkflowConfig();

  if (reviewers.length === 0) return null;

  return (
    <Box sx={{ pt: 2, mt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box
          sx={(theme) => ({
            width: 28,
            height: 28,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
          })}
        >
          <RavenIcon icon={Users} sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
        >
          Assigned Reviewers
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          ({reviewers.length})
        </Typography>
        <Box
          component="span"
          sx={{ color: "text.secondary", display: "inline-flex" }}
        >
          <RavenIcon
            icon={ChevronDown}
            style={{
              transition: "transform .15s ease",
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
        </Box>
      </Stack>

      <Collapse in={open}>
        {/* No inner scroll — the rail's CardContent is the scroll region,
            so the roster flows and scrolls with Status History above it. */}
        {/* The committees sit side by side when the rail is wide enough for
            two name columns, and stack when it is not. A container query,
            not a breakpoint: the rail's width follows the 7fr/3fr detail
            grid, the drawer and the viewport all at once. */}
        <Box sx={{ mt: 1.5, containerType: "inline-size" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr)",
              columnGap: 2,
              rowGap: 1.5,
              [`@container (min-width: ${TWO_COLUMN_MIN}px)`]: {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            {COMMITTEE_ORDER.map((c) => {
              const forCommittee = reviewers.filter(
                (r) => r.committee === c.value,
              );
              if (forCommittee.length === 0) return null;
              return (
                <Box key={c.value} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    {workflow?.committees?.[c.value]?.label ?? c.label}
                  </Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {forCommittee.map((r) => {
                      const name = r.user_name ?? r.user_id;
                      return (
                        <Stack
                          key={`${r.user_id}-${r.committee}-${r.slot ?? r.sub_role}`}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ py: 0.5 }}
                        >
                          <Box
                            aria-hidden="true"
                            sx={(theme) => ({
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: "primary.dark",
                              display: "grid",
                              placeItems: "center",
                              fontSize: 10,
                              fontWeight: 600,
                              flexShrink: 0,
                            })}
                          >
                            {reviewerInitials(r.user_name)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              title={name}
                              sx={{
                                fontSize: 12.5,
                                lineHeight: 1.35,
                                fontWeight: 500,
                                color: "text.primary",
                                textTransform: "capitalize",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {name}
                            </Typography>
                            <Tooltip
                              title={reviewerSlotLabel(r, workflow?.committees)}
                              placement="top"
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  lineHeight: 1.35,
                                  color: "text.secondary",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {reviewerSlotLabel(r, workflow?.committees)}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
